/**
 * Overview dashboard · pure data derivations.
 *
 * Powers the «Обзор» bento (HANDOFF_board_overview.md · P2). Every widget number
 * is computed from real `Progress` state here — no static/lying sources (Genie:
 * «вся статистика реально считается»). Functions are pure and unit-tested
 * (`__tests__/overviewData.test.ts`) so the data-smoke fixture can assert exact
 * numbers.
 *
 * `nowMs` is always passed in (never `Date.now()` inside) so tests are
 * deterministic and callers control the clock.
 */

import type { CockpitState, ISTQBChapterRef, Progress } from '@shared/schemas';

const DAY_MS = 86_400_000;

/* ========================================================================= *
 *  ISTQB · готовность по главам (P3.4)
 *
 *  ЧЕСТНЫЙ источник — только моки (mock1/mock2) и экзамены-вехи
 *  (blockExamResult + blockExamResults[*]). НЕ sprint.istqb (это статика).
 *  Агрегируем correct/total по каждой главе через все прогоны, pct = сумма.
 * ========================================================================= */

export const ISTQB_CHAPTERS: ReadonlyArray<ISTQBChapterRef> = [
  'CTFL-Ch1',
  'CTFL-Ch2',
  'CTFL-Ch3',
  'CTFL-Ch4',
  'CTFL-Ch5',
  'CTFL-Ch6',
];

export interface ChapterReadiness {
  chapter: ISTQBChapterRef;
  /** Short label «Ch 1». */
  label: string;
  correct: number;
  total: number;
  /** 0-100 rounded · 0 when no data. */
  pct: number;
  /** True when at least one graded question touched this chapter. */
  hasData: boolean;
}

export interface IstqbReadiness {
  chapters: ReadonlyArray<ChapterReadiness>;
  /** True when ANY chapter has graded data (drives empty-state). */
  anyData: boolean;
  /** Weakest chapter WITH data (lowest pct) · undefined when no data. */
  weakest?: ChapterReadiness;
}

/**
 * Aggregate ISTQB per-chapter readiness across every graded run in cockpit.
 * Sources merged: mockHistory.mock1/mock2 · blockExamResult · blockExamResults[*].
 */
export function aggregateIstqbByChapter(cockpit: CockpitState): IstqbReadiness {
  const acc = new Map<ISTQBChapterRef, { correct: number; total: number }>();
  for (const ch of ISTQB_CHAPTERS) acc.set(ch, { correct: 0, total: 0 });

  const sources = [
    cockpit.mockHistory.mock1?.byChapter,
    cockpit.mockHistory.mock2?.byChapter,
    cockpit.blockExamResult?.byChapter,
    ...Object.values(cockpit.blockExamResults ?? {}).map((r) => r.byChapter),
  ];

  for (const byChapter of sources) {
    if (!byChapter) continue;
    for (const ch of ISTQB_CHAPTERS) {
      const cell = byChapter[ch];
      if (!cell) continue;
      const bucket = acc.get(ch)!;
      bucket.correct += cell.correct;
      bucket.total += cell.total;
    }
  }

  const chapters: ChapterReadiness[] = ISTQB_CHAPTERS.map((ch, i) => {
    const { correct, total } = acc.get(ch)!;
    return {
      chapter: ch,
      label: `Ch ${i + 1}`,
      correct,
      total,
      pct: total > 0 ? Math.round((correct / total) * 100) : 0,
      hasData: total > 0,
    };
  });

  const withData = chapters.filter((c) => c.hasData);
  const weakest = withData.length
    ? withData.reduce((lo, c) => (c.pct < lo.pct ? c : lo))
    : undefined;

  return { chapters, anyData: withData.length > 0, weakest };
}

/* ========================================================================= *
 *  Закрытия за N дней (дневной группировщик · модель deriveActivityWeeksLocal)
 *
 *  Тикеты (done) + домашки (done) по дню completedAt, окно последних N дней.
 *  Возвращает ровно N ячеек (старые → новые), включая нулевые дни.
 * ========================================================================= */

export interface DailyClosureBar {
  /** Local day key `YYYY-MM-DD`. */
  dayKey: string;
  /** Tickets + homework closed that day. */
  count: number;
  /** True for the last cell (today). */
  isToday: boolean;
  /** Offset from today in days (0 = today, N-1 = oldest). */
  daysAgo: number;
}

export interface DailyClosures {
  bars: ReadonlyArray<DailyClosureBar>;
  /** Peak count across the window (≥1 for safe normalization). */
  max: number;
  /** Total closures across the window. */
  total: number;
}

function localDayKey(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/** Start-of-local-day ms for a given instant. */
function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Bucket done tickets + homework by completion day over the last `days` days.
 * `nowMs` fixes «today»; window is inclusive of today, so length === days.
 */
export function deriveDailyClosures(
  progress: Pick<Progress, 'tickets' | 'homework'>,
  nowMs: number,
  days = 14,
): DailyClosures {
  const counts = new Map<string, number>();
  const bump = (completedAt: string | undefined, status: string) => {
    if (status !== 'done' || !completedAt) return;
    const t = Date.parse(completedAt);
    if (!Number.isFinite(t)) return;
    const key = localDayKey(t);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  };
  for (const t of Object.values(progress.tickets)) bump(t.completedAt, t.status);
  for (const h of Object.values(progress.homework)) bump(h.completedAt, h.status);

  const todayStart = startOfDay(nowMs);
  const bars: DailyClosureBar[] = [];
  let total = 0;
  let max = 0;
  for (let i = days - 1; i >= 0; i--) {
    const dayStart = todayStart - i * DAY_MS;
    const key = localDayKey(dayStart);
    const count = counts.get(key) ?? 0;
    total += count;
    if (count > max) max = count;
    bars.push({ dayKey: key, count, isToday: i === 0, daysAgo: i });
  }
  return { bars, max: Math.max(1, max), total };
}

/* ========================================================================= *
 *  Ритм 7 дней · стрик · помидоры · лучший день недели
 * ========================================================================= */

export interface RhythmDay {
  dayKey: string;
  /** ≥1 pomodoro that day. */
  active: boolean;
  isToday: boolean;
}

export interface RhythmSummary {
  /** 7 cells oldest → newest. */
  days: ReadonlyArray<RhythmDay>;
  /** Pomodoro sessions in the last 7 days. */
  weekPomodoros: number;
  /** Weekday index 0=Sun..6=Sat with the most pomodoros ever · null when none. */
  bestWeekday: number | null;
}

const WEEKDAY_RU = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];

/** Human weekday label (nominative) for a 0=Sun..6=Sat index. */
export function weekdayLabelRu(idx: number | null): string {
  return idx === null ? '' : (WEEKDAY_RU[idx] ?? '');
}

/**
 * 7-day rhythm strip from pomodoro sessions.
 * `bestWeekday` argmaxes over ALL sessions (not just the window).
 */
export function derive7DayRhythm(
  sessions: ReadonlyArray<{ at: number; startHour: number }>,
  nowMs: number,
): RhythmSummary {
  const activeDays = new Set<string>();
  const weekdayCounts = new Array<number>(7).fill(0);
  const weekCutoff = startOfDay(nowMs) - 6 * DAY_MS;
  let weekPomodoros = 0;

  for (const s of sessions) {
    if (!Number.isFinite(s.at)) continue;
    const day = new Date(s.at);
    weekdayCounts[day.getDay()] = (weekdayCounts[day.getDay()] ?? 0) + 1;
    activeDays.add(localDayKey(s.at));
    if (startOfDay(s.at) >= weekCutoff) weekPomodoros += 1;
  }

  const todayStart = startOfDay(nowMs);
  const days: RhythmDay[] = [];
  for (let i = 6; i >= 0; i--) {
    const dayStart = todayStart - i * DAY_MS;
    const key = localDayKey(dayStart);
    days.push({ dayKey: key, active: activeDays.has(key), isToday: i === 0 });
  }

  let bestWeekday: number | null = null;
  let best = 0;
  for (let d = 0; d < 7; d++) {
    const c = weekdayCounts[d] ?? 0;
    if (c > best) {
      best = c;
      bestWeekday = d;
    }
  }

  return { days, weekPomodoros, bestWeekday };
}

/* ========================================================================= *
 *  Журнал недели · из progress.events[] (окно 7 дней)
 * ========================================================================= */

export interface JournalRow {
  id: string;
  at: string;
  /** Human one-liner. */
  text: string;
  /** Visual tag bucket. */
  tag: 'close' | 'exam' | 'start' | 'badge';
}

interface EventLike {
  id: string;
  at: string;
  kind: string;
  payload: Record<string, unknown>;
}

function payloadStr(payload: Record<string, unknown>, key: string): string {
  const v = payload[key];
  return typeof v === 'string' ? v : '';
}
function payloadNum(payload: Record<string, unknown>, key: string): number | null {
  const v = payload[key];
  return typeof v === 'number' ? v : null;
}

/**
 * Map the rich event stream to a week's journal rows (newest first, capped).
 * Recognizes the P3.1 event kinds; unknown kinds are skipped.
 */
export function deriveWeekJournal(
  events: ReadonlyArray<EventLike> | undefined,
  nowMs: number,
  limit = 6,
): ReadonlyArray<JournalRow> {
  if (!events || events.length === 0) return [];
  const cutoff = nowMs - 7 * DAY_MS;
  const rows: JournalRow[] = [];

  for (const e of events) {
    const t = Date.parse(e.at);
    if (!Number.isFinite(t) || t < cutoff) continue;
    const row = mapEventToRow(e);
    if (row) rows.push(row);
  }

  rows.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  return rows.slice(0, limit);
}

function mapEventToRow(e: EventLike): JournalRow | null {
  const base = { id: e.id, at: e.at };
  switch (e.kind) {
    case 'ticket.done': {
      const id = payloadStr(e.payload, 'ticketId');
      return { ...base, text: `${id || 'Тикет'} закрыт`, tag: 'close' };
    }
    case 'ticket.in-progress': {
      const id = payloadStr(e.payload, 'ticketId');
      return { ...base, text: `${id || 'Тикет'} в работе`, tag: 'start' };
    }
    case 'homework.done': {
      const id = payloadStr(e.payload, 'homeworkId');
      return { ...base, text: `${id || 'Домашка'} сдана`, tag: 'close' };
    }
    case 'pomodoro.done':
      return { ...base, text: 'Помидор завершён', tag: 'start' };
    case 'exam.mock': {
      const pct = payloadNum(e.payload, 'pct');
      const slot = payloadStr(e.payload, 'slot');
      return {
        ...base,
        text: `Mock ${slot || ''}${pct !== null ? ` · ${pct}%` : ''}`.trim(),
        tag: 'exam',
      };
    }
    case 'exam.block': {
      const pct = payloadNum(e.payload, 'pct');
      return {
        ...base,
        text: `Экзамен-веха${pct !== null ? ` · ${pct}%` : ''}`,
        tag: 'exam',
      };
    }
    case 'badge.earned': {
      const id = payloadStr(e.payload, 'badgeId');
      return { ...base, text: `Бейдж: ${id || 'новый'}`, tag: 'badge' };
    }
    default:
      return null;
  }
}

/* ========================================================================= *
 *  Курс · печати блоков (экзамены-вехи)
 * ========================================================================= */

export interface BlockStamp {
  /** Block number 1-3. */
  n: number;
  /** Short label «Б1». */
  label: string;
  /** Exam pct when taken · null otherwise. */
  pct: number | null;
  status: 'done' | 'none';
}

/** Block «печати» from checkpoint-exam results (bankId `block{N}-exam`). */
export function deriveBlockStamps(cockpit: CockpitState): ReadonlyArray<BlockStamp> {
  const results = cockpit.blockExamResults ?? {};
  return [1, 2, 3].map((n) => {
    const r = results[`block${n}-exam`];
    const pct = r ? r.pct : null;
    return {
      n,
      label: `Б${n}`,
      pct,
      status: pct !== null ? 'done' : 'none',
    };
  });
}

/* ========================================================================= *
 *  Стрик + ближайшая веха — общие для экрана «Обзор» и вкладки «обзор» на доске
 *  (Sprint board-tab). Pure (nowMs / sprints injected) — переиспользуемы и тестируемы.
 * ========================================================================= */

/** Consecutive-days streak (walking back from `nowMs`) with ≥1 closed ticket. */
export function computeStreakDays(
  tickets: Record<string, { status: string; completedAt?: string }>,
  nowMs: number,
): number {
  const days = new Set<string>();
  for (const t of Object.values(tickets)) {
    if (t.status !== 'done' || !t.completedAt) continue;
    const at = Date.parse(t.completedAt);
    if (Number.isFinite(at)) days.add(localDayKey(at));
  }
  if (days.size === 0) return 0;
  const todayStart = startOfDay(nowMs);
  let streak = 0;
  for (let i = 0; i < 365; i++) {
    if (days.has(localDayKey(todayStart - i * DAY_MS))) streak += 1;
    else if (streak > 0) break;
  }
  return streak;
}

export interface NearestMilestoneInfo {
  title: string;
  afterLabel: string;
  eta?: string;
  ticketsLeft: number;
}

/** Pedagogical release milestones (mirror of Progress.tsx MILESTONES_P_SPEC). */
const MILESTONE_SPEC: ReadonlyArray<{ title: string; afterOrd: number; afterLabel: string }> = [
  { title: 'Trainee QA · v0.5', afterOrd: 6, afterLabel: 'Sprint 06' },
  { title: 'Junior QA · v1.0', afterOrd: 12, afterLabel: 'Sprint 12' },
  { title: 'Middle QA · v2.0', afterOrd: 17, afterLabel: 'Sprint 17' },
];

function estimateMilestoneEta(currentOrd: number, targetOrd: number): string | undefined {
  if (currentOrd >= targetOrd) return undefined;
  const left = targetOrd - currentOrd + 1;
  if (left <= 1) return '~1 неделя';
  if (left <= 4) return `~${left} нед`;
  return `~${Math.ceil(left / 4)} мес`;
}

/** First not-yet-reached milestone + tickets open до его гейта (Σ open ≤ gate ord). */
export function deriveNearestMilestone(
  progress: Pick<Progress, 'sprints' | 'tickets'>,
  sprints: ReadonlyArray<{ id: string; ord: number; ticketIds: ReadonlyArray<string> }>,
  currentOrd: number,
): NearestMilestoneInfo | undefined {
  const next = MILESTONE_SPEC.find((m) => {
    const s = sprints.find((sp) => sp.ord === m.afterOrd);
    return (s ? progress.sprints[s.id]?.status : undefined) !== 'completed';
  });
  if (!next) return undefined;
  let ticketsLeft = 0;
  for (const s of sprints) {
    if (s.ord > next.afterOrd) continue;
    for (const id of s.ticketIds) {
      if (progress.tickets[id]?.status !== 'done') ticketsLeft += 1;
    }
  }
  return {
    title: next.title,
    afterLabel: next.afterLabel,
    eta: estimateMilestoneEta(currentOrd, next.afterOrd),
    ticketsLeft,
  };
}
